import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import collectiveApi from 'modules/common/collectiveApi';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

const PATH = '/bookkeeping/v2/bank-statements/reassign-periods/';

/**
 * One correction: which tracked statement to move, and where to.
 *
 * `sourcePeriod` names the tracker row rather than the document, because a single PDF can carry
 * several monthly statements and hold a row for each. Without it the server can only be told
 * "this document is for March", which for such a file means discarding the months it is not
 * being asked about. It is absent for a document holding no row at all — a file we failed to
 * read, or one that lost its month to another upload — where there is nothing to move and the
 * member is naming a month for the first time.
 */
export interface StatementPeriodAssignment {
    documentUuid: string;
    sourcePeriod?: StatementPeriod;
    /** The month it should count for, or `null` to undo the correction. */
    targetPeriod: StatementPeriod | null;
}

/** The wire shape of one assignment. Applied below, so a renamed field fails type-check. */
interface PeriodAssignmentRequest {
    member_document_uuid: string;
    /** Null on both to undo a correction, putting the statement back on the month we read. */
    year: number | null;
    month: number | null;
    /**
     * The month the row being moved currently holds. Null for a document with no row, and for a
     * client that cannot name one — the server then falls back to the document's single row and
     * refuses if it carries more than one.
     */
    from_year: number | null;
    from_month: number | null;
}

interface ReassignStatementPeriodsParams {
    memberId: number;
    assignments: StatementPeriodAssignment[];
    /**
     * The card the corrections were made from, sent so a file we could not read is still
     * assignable: with no extraction to rebuild a tracker row from, this is the member's own
     * answer, and the server refuses without it.
     */
    bankAccountId?: string | null;
}

/**
 * Set which month each tracked statement counts for, as one request. Batched because the
 * corrections only make sense together: moving a file onto a month takes it off another. Sent
 * one at a time, that middle state is committed and the displaced row really is deleted.
 *
 * Resolves to `void` — the response carries nothing callers need, and they refetch coverage.
 */
export default function useReassignStatementPeriods() {
    return useMutation<void, AxiosError, ReassignStatementPeriodsParams>({
        mutationFn: async (params) => {
            await collectiveApi.post(PATH, {
                member_id: params.memberId,
                bank_account_id: params.bankAccountId ?? null,
                assignments: params.assignments.map(
                    ({ documentUuid, sourcePeriod, targetPeriod }): PeriodAssignmentRequest => ({
                        member_document_uuid: documentUuid,
                        year: targetPeriod?.year ?? null,
                        month: targetPeriod?.month ?? null,
                        from_year: sourcePeriod?.year ?? null,
                        from_month: sourcePeriod?.month ?? null,
                    })
                ),
            });
        },
        // Invalidation is the caller's: the statement surfaces refetch coverage on their own
        // terms, and this endpoint is not the only thing that moves held periods.
    });
}

interface RemoveStatementTrackingParams {
    memberId: number;
    documentUuid: string;
    /** The card to remove the file from; other accounts keep theirs. Null (no Tight id) takes the whole file. */
    bankAccountId: string | null;
    /** The month to remove, as tracked on the card. Omitted, every month the file holds there goes. */
    period?: StatementPeriod;
}

/**
 * Take an uploaded statement off one account card. The server soft-deletes the document once no
 * account holds it, and re-homes it when its upload card holds none — there is no separate DELETE.
 *
 * A separate hook because a removal carries no target month (`from_year`/`from_month` name which
 * statement, not where it goes), and kept in this module so a test mocking it fails loudly.
 *
 * Worth sending even when the document holds nothing: the server's removal marker stops an
 * in-flight extraction from later creating the row. Idempotent; a 409 (already in the books, or
 * bank-delivered) removes nothing.
 */
export function useRemoveStatementTracking() {
    return useMutation<void, AxiosError, RemoveStatementTrackingParams>({
        mutationFn: async ({ memberId, documentUuid, bankAccountId, period }) => {
            await collectiveApi.post(PATH, {
                member_id: memberId,
                bank_account_id: bankAccountId,
                assignments: [
                    {
                        member_document_uuid: documentUuid,
                        remove: true,
                        // Both or neither — the server refuses half a month.
                        from_year: period?.year ?? null,
                        from_month: period?.month ?? null,
                    },
                ],
            });
        },
        // Invalidation is the caller's, matching `useReassignStatementPeriods`: what to refresh,
        // and what to tell the member about the file afterwards, is the surface's to decide.
    });
}
